import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, mergeProps as _mergeProps, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "modal"
}
const _hoisted_2 = { key: 0 }

import { useStore } from 'vuex';
import { ref, onMounted, computed, watch, nextTick, useAttrs } from 'vue';

import { EPINIO_TYPES } from '../types';
import { Card } from '@components/Card';
import Banner from '@components/Banner/Banner.vue';
import { _CREATE } from '@shell/config/query-params';
import AsyncButton from '@shell/components/AsyncButton';
import ResourceTable from '@shell/components/ResourceTable';
import Masthead from '@shell/components/ResourceList/Masthead';
import { epinioExceptionToErrorsArray } from '../utils/errors';
import LabeledInput from '@components/Form/LabeledInput/LabeledInput.vue';
import { validateKubernetesName } from '@shell/utils/validators/kubernetes-name';


export default /*@__PURE__*/_defineComponent({
  __name: 'namespaces',
  props: {
    schema: {},
    rows: {}
  },
  setup(__props: any) {

const props = __props; // eslint-disable-line @typescript-eslint/no-unused-vars

const attrs = useAttrs();
const store = useStore();
const t = store.getters['i18n/t'];

const errors = ref<Array>([]);
const namespaceName = ref('namespaceName');
const showCreateModal = ref<boolean>(false);
const creatingNamespace = ref<boolean>(false);
const touched = ref<boolean>(false);

const mode: string = _CREATE;
const resource: string = EPINIO_TYPES.NAMESPACE;
const value = ref<Array>({ meta: { name: '' } });

const showPromptRemove = computed(() => { 
  return store.state['action-menu'].showPromptRemove
});

const validationPassed = computed(() => {
  // Add here fields that need validation
  if (!creatingNamespace.value) {
    errors.value = []; // eslint-disable-line vue/no-side-effects-in-computed-properties
    errors.value = getNamespaceErrors(value.value.meta.name); // eslint-disable-line vue/no-side-effects-in-computed-properties
  }

  return errors.value?.length === 0;
});

onMounted(() => {
  // Opens the create namespace modal if the query is passed as query param
  if (store.$router.currentRoute._value.query.mode === 'openModal') {
    openCreateModal();
  }
});

watch(
  () => showPromptRemove,
  (newState, oldState) => {
    if (oldState === true && newState === false) {
      // Refetch apps when namespace is deleted
      store.dispatch('findAll', { type: 'applications', opt: { force: true } });
    }
  }
);

// Watch for changes in value.meta.name, not needed as there are no rules currently
watch(
  () => value.value.meta.name,
  () => {
    creatingNamespace.value = false;
    validateNamespace(value.value.meta.name);
  }
);

async function openCreateModal() {
  showCreateModal.value = true;
  // Focus on the name input field... after it's been displayed
  nextTick(() => namespaceName.value.focus());
  // Create a skeleton namespace
  value.value = await store.dispatch(
    `epinio/create`, 
    { type: EPINIO_TYPES.NAMESPACE },
  );
}

function closeCreateModal() {
  showCreateModal.value = false;
  errors.value = [];
  touched.value = false;
}

async function onSubmit(buttonCb) {
  creatingNamespace.value = true;
  try {
    await value.value.create();
    closeCreateModal();
    buttonCb(true);
    touched.value = false;
  } catch (e) {
    errors.value = [];
    errors.value = epinioExceptionToErrorsArray(e).map(JSON.stringify);
    buttonCb(false);
  }
}

function validateNamespace(name) {
  if (!name?.length && !touched.value) {
    touched.value = true;
  }

  errors.value = getNamespaceErrors(name);
}

function getNamespaceErrors(name) {
  const kubernetesErrors = validateKubernetesName(
    name || '', 
    t('epinio.namespace.name'), 
    store.getters, 
    undefined, 
    [],
  );

  if (kubernetesErrors.length) {
    return [kubernetesErrors.join(', ')];
  }

  const validateName = name.match(/[a-z0-9]([-a-z0-9]*[a-z0-9])?/);

  if (
    !validateName || 
    validateName[0] !== name && 
    !errors.value.includes(t('epinio.namespace.validations.name'))
  ) {
    return [t('epinio.namespace.validations.name')];
  }

  return [];
}

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(Masthead), {
      schema: _ctx.schema,
      resource: _unref(resource)
    }, {
      createButton: _withCtx(() => [
        _createElementVNode("button", {
          class: "btn role-primary",
          onClick: openCreateModal
        }, _toDisplayString(_unref(t)('generic.create')), 1)
      ]),
      _: 1
    }, 8, ["schema", "resource"]),
    _cache[5] || (_cache[5] = _createTextVNode()),
    _createVNode(_unref(ResourceTable), _mergeProps(_unref(attrs), {
      rows: _ctx.rows,
      groupable: false,
      schema: _ctx.schema,
      "key-field": "_key",
      useQueryParamsForSimpleFiltering: true
    }), null, 16, ["rows", "schema"]),
    _cache[6] || (_cache[6] = _createTextVNode()),
    (showCreateModal.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(_unref(Card), {
            class: "modal-content",
            "show-actions": true
          }, {
            title: _withCtx(() => [
              _withDirectives(_createElementVNode("h4", null, null, 512), [
                [_directive_clean_html, _unref(t)('epinio.namespace.create')]
              ])
            ]),
            body: _withCtx(() => [
              _createVNode(LabeledInput, {
                ref_key: "namespaceName",
                ref: namespaceName,
                value: value.value.meta.name,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((value.value.meta.name) = $event)),
                label: _unref(t)('epinio.namespace.name'),
                required: true
              }, null, 8, ["value", "label"]),
              _cache[1] || (_cache[1] = _createTextVNode()),
              (touched.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(errors.value, (err, i) => {
                      return (_openBlock(), _createBlock(Banner, {
                        key: i,
                        color: "error",
                        label: err
                      }, null, 8, ["label"]))
                    }), 128))
                  ]))
                : _createCommentVNode("", true)
            ]),
            actions: _withCtx(() => [
              _createElementVNode("button", {
                class: "btn role-secondary mr-10",
                onClick: closeCreateModal
              }, _toDisplayString(_unref(t)('generic.cancel')), 1),
              _cache[2] || (_cache[2] = _createTextVNode()),
              _createVNode(_unref(AsyncButton), {
                disabled: !validationPassed.value,
                mode: _unref(mode),
                onClick: onSubmit
              }, null, 8, ["disabled", "mode"])
            ]),
            _: 1,
            __: [3,4]
          })
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})